import { z } from 'zod';

export const searchRangeExtentsSchema = z.object({
  revenue_max: z.number().finite(),
  aktiva_max: z.number().finite(),
  profit_min: z.number().finite(),
  profit_max: z.number().finite(),
  employees_max: z.number().finite(),
});
export type SearchRangeExtents = z.infer<typeof searchRangeExtentsSchema>;
export const FALLBACK_RANGE_EXTENTS: SearchRangeExtents = {
  revenue_max: 1e12,
  aktiva_max: 1e13,
  profit_min: -1e10,
  profit_max: 1e11,
  employees_max: 1e6,
};

/** Multiplication avoids log10 rounding at exact powers of ten. */
export function roundUpPow10(x: number): number {
  if (!Number.isFinite(x) || x <= 1) return 1;
  let power = 1;
  while (power < x) power *= 10;
  return power;
}

function positiveStops(max: number, first: number): number[] {
  const ceiling = roundUpPow10(max);
  const stops: number[] = [];
  for (let base = first; base <= ceiling; base *= 10) {
    for (const multiplier of [1, 2, 5]) {
      const value = base * multiplier;
      if (value <= ceiling) stops.push(value);
    }
  }
  if (stops[stops.length - 1] !== ceiling) stops.push(ceiling);
  return stops;
}

export function buildStops({
  maxAbsNeg = 0,
  maxPos,
  first,
}: {
  maxAbsNeg?: number;
  maxPos: number;
  first: number;
}): number[] {
  if (!Number.isFinite(first) || first <= 0)
    throw new Error('first must be positive and finite');
  const negative =
    maxAbsNeg > 0
      ? positiveStops(maxAbsNeg, first)
          .reverse()
          .map((n) => -n)
      : [];
  return [...negative, 0, ...positiveStops(maxPos, first)];
}

/** Display mapping only: the caller retains the original exact bound in state. */
export function valueToIndex(
  value: number | undefined,
  stops: readonly number[],
  side: 'min' | 'max',
): number {
  if (value === undefined) return side === 'min' ? 0 : stops.length - 1;
  const upper = stops.findIndex((stop) => stop >= value);
  if (upper < 0) return stops.length - 1;
  if (stops[upper] === value || upper === 0 || side === 'max') return upper;
  return upper - 1;
}

export function indexToValue(
  index: number,
  stops: readonly number[],
  side: 'min' | 'max',
): number | undefined {
  if (
    (side === 'min' && index === 0) ||
    (side === 'max' && index === stops.length - 1)
  )
    return undefined;
  return stops[index];
}

/** Accept grouped digits and decimal quantities with k/Tsd, m/Mio, b/bn/Mrd. */
export function parseAmount(text: string): number | undefined {
  const match = text
    .trim()
    .match(/^([+−-]?[\d.,\s'’]+)\s*(k|tsd|m|mio|b|bn|mrd)?\.?$/i);
  if (!match) return undefined;
  const suffix = match[2]?.toLowerCase();
  let digits = match[1].trim().replace('−', '-');
  // Spaces and apostrophes must be valid thousands grouping.
  if (/[\s'’]/.test(digits)) {
    if (!/^[+-]?\d{1,3}(?:[\s'’]\d{3})+(?:[.,]\d+)?$/.test(digits))
      return undefined;
    digits = digits.replace(/[\s'’]/g, '');
  }
  if (digits.includes(',') && digits.includes('.')) {
    const decimal =
      digits.lastIndexOf(',') > digits.lastIndexOf('.') ? ',' : '.';
    const group = decimal === ',' ? '.' : ',';
    const escaped = group === '.' ? '\\.' : ',';
    const pattern = new RegExp(
      `^[+-]?\\d{1,3}(?:${escaped}\\d{3})+${decimal === '.' ? '\\.' : ','}\\d+$`,
    );
    if (!pattern.test(digits)) return undefined;
    digits = digits.split(group).join('').replace(decimal, '.');
  } else if (/^[+-]?\d{1,3}(?:[.,]\d{3})+$/.test(digits)) {
    digits = digits.replace(/[.,]/g, '');
  } else {
    if (!/^[+-]?\d+(?:[.,]\d+)?$/.test(digits)) return undefined;
    digits = digits.replace(',', '.');
  }
  const factor =
    suffix === 'k' || suffix === 'tsd'
      ? 1e3
      : suffix === 'm' || suffix === 'mio'
        ? 1e6
        : suffix
          ? 1e9
          : 1;
  const amount = Number(digits) * factor;
  return Number.isFinite(amount) ? Math.round(amount) : undefined;
}
